/**
 * AddEditAuthorV2 — V2 port of the Author edit form. Same Redux
 * fetch / submit graph as v1 so the API contract is byte-identical:
 *   • fetchOneAuthor → state.authors.currentAuthor
 *   • addEditAuthor(JSON.stringify(payload), navigate)
 *   • deleteAuthor(id, navigate)
 *
 * Sections:
 *   01 Basic info        (name req / title)
 *   02 Social            (Facebook / X / Instagram URLs)
 *   03 Images            (profile + signature UploadTiles)
 *   04 Bio               (Quill rich-text editor, unchanged)
 *   05 Caption aliases   (edit-only — v1 AuthorCaptionAliases
 *                         inline, captions ops use it as-is)
 *   06 Danger zone       (edit-only)
 *
 * The Quill bio editor (`Editor` primitive) keeps its v1 imports —
 * restyling Quill chrome is out of scope here.
 *
 * Exposes a `submit()` method via ref for the v1 imperative path;
 * V2 page chrome drives the form via `form="author-form"` + a
 * `type="submit"` button.
 */
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';

import LoadingBackdrop from '../../Common/LoadingBackdrop';
import Editor from '../../Common/Editor';
import AuthorCaptionAliases from '../../AuthorCaptionAliases/AuthorCaptionAliases';
import {
  fetchOneAuthor,
  addEditAuthor,
  deleteAuthor,
  clearAuthors,
} from '../../../actions/authorsActions';
import { uploadImage, clearMedia } from '../../../actions/mediaActions';
import { preventEnterFormSubmission } from '../../../utils/formUtils';

import { Button } from '../../redesign';
import {
  FormSection,
  FieldBlock,
  FieldRow,
  TextInput,
  UploadTile,
} from '../../redesign/edit';

const AddEditAuthorV2 = forwardRef(function AddEditAuthorV2(
  { isEdit, selectedId, onLoaded },
  ref,
) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentAuthor = useSelector((s) => s.authors?.currentAuthor);
  const editorRef = useRef(null);

  const [state, setState] = useState({
    author_name: '',
    author_title: '',
    author_image: '',
    author_signature: '',
    author_signature_preview: null,
    author_bio:
      '<p style="font-family: Asap-Regular; font-size: 16px; color: #545454;"><br></p>',
    author_facebook_url: '',
    author_twitter_url: '',
    author_instagram_url: '',
    author_image_preview: null,
    author_is_active: 1,
    isSetToEdit: false,
  });

  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Latest-value ref for onLoaded — keeps the load effect from
  // re-firing when the parent re-renders with a fresh callback.
  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;

  /* ─── Fetch on mount (1:1 with v1) ─────────────────────────── */
  useEffect(() => {
    if (!isEdit || !selectedId) return undefined;
    setLoading(true);
    dispatch(fetchOneAuthor(selectedId))
      .catch((error) => {
        console.error('Error fetching author:', error);
        Swal.fire({ icon: 'warning', text: 'Error finding selected Author' });
      })
      .finally(() => setLoading(false));
    return undefined;
  }, [dispatch, isEdit, selectedId]);

  /* Clear currentAuthor on unmount (1:1 with v1). */
  useEffect(() => () => dispatch(clearAuthors()), [dispatch]);

  /* Hydrate form state once `currentAuthor` arrives. */
  useEffect(() => {
    if (!isEdit || !currentAuthor) return;
    if (String(currentAuthor.author_id) !== String(selectedId)) return;
    setState((prev) => ({
      ...prev,
      ...currentAuthor,
      author_bio: currentAuthor.author_bio || prev.author_bio,
      author_image: currentAuthor.author_image || '',
      author_signature: currentAuthor.author_signature || '',
      author_facebook_url: currentAuthor.author_facebook_url || '',
      author_twitter_url: currentAuthor.author_twitter_url || '',
      author_instagram_url: currentAuthor.author_instagram_url || '',
      isSetToEdit: true,
    }));

    // Hand title + updated_at up to the page chrome (uniform with
    // the other V2 edit pages — one fetch per page load).
    if (onLoadedRef.current) {
      onLoadedRef.current({
        author_name: currentAuthor.author_name,
        author_updated_at:
          currentAuthor.author_updated_at ?? currentAuthor.updated_at ?? null,
      });
    }
  }, [isEdit, currentAuthor, selectedId]);

  /* ─── Preview-URL cleanup on unmount ───────────────────────── */
  const previewUrlsRef = useRef({});
  previewUrlsRef.current.author_image_preview = state.author_image_preview;
  previewUrlsRef.current.author_signature_preview = state.author_signature_preview;
  useEffect(() => {
    return () => {
      const refs = previewUrlsRef.current;
      if (refs.author_image_preview) window.URL.revokeObjectURL(refs.author_image_preview);
      if (refs.author_signature_preview) window.URL.revokeObjectURL(refs.author_signature_preview);
    };
  }, []);

  /* ─── Handlers ─────────────────────────────────────────────── */
  const setField = (name, value) => setState((prev) => ({ ...prev, [name]: value }));
  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setField(name, value);
  };
  const handleBioChange = (html) => setField('author_bio', html);
  const handleSelectionChange = () => {};

  const handleImageUpload = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (state.author_image_preview) window.URL.revokeObjectURL(state.author_image_preview);
    setState((prev) => ({
      ...prev,
      author_image_preview: window.URL.createObjectURL(file),
    }));
    dispatch(uploadImage(file)).then((response) => {
      setState((prev) => ({ ...prev, author_image: response.data.image_url }));
      dispatch(clearMedia());
    });
  }, [state.author_image_preview, dispatch]);

  const handleSignatureUpload = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (state.author_signature_preview) window.URL.revokeObjectURL(state.author_signature_preview);
    setState((prev) => ({
      ...prev,
      author_signature_preview: window.URL.createObjectURL(file),
    }));
    dispatch(uploadImage(file)).then((response) => {
      setState((prev) => ({ ...prev, author_signature: response.data.image_url }));
      dispatch(clearMedia());
    });
  }, [state.author_signature_preview, dispatch]);

  /* ─── Submit (1:1 with v1) ──────────────────────────────── */
  const submit = useCallback(() => {
    if (!state.author_name) {
      Swal.fire('Error', 'Please enter author name', 'error');
      return;
    }
    setIsSaving(true);
    try {
      const payload = {
        author_name: state.author_name,
        author_title: state.author_title,
        author_image: state.author_image,
        author_signature: state.author_signature,
        author_bio: state.author_bio,
        author_facebook_url: state.author_facebook_url,
        author_twitter_url: state.author_twitter_url,
        author_instagram_url: state.author_instagram_url,
        author_is_active: state.author_is_active,
        ...(isEdit && { author_id: selectedId }),
      };
      const result = dispatch(addEditAuthor(JSON.stringify(payload), () => navigate('/authors')));
      if (result && typeof result.finally === 'function') {
        result.finally(() => setIsSaving(false));
      } else {
        setTimeout(() => setIsSaving(false), 3000);
      }
    } catch (error) {
      console.error('Error saving author:', error);
      Swal.fire('Error', 'Failed to save author', 'error');
      setIsSaving(false);
    }
  }, [state, isEdit, selectedId, dispatch, navigate]);

  const handleSubmit = (e) => {
    if (e?.preventDefault) e.preventDefault();
    submit();
  };

  useImperativeHandle(ref, () => ({ submit }), [submit]);

  /* ─── Delete (1:1 with v1) ──────────────────────────────── */
  const handleDelete = () => {
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(deleteAuthor(selectedId, () => navigate('/authors')));
      }
    });
  };

  if (isEdit && loading) {
    return <LoadingBackdrop open />;
  }
  if (isEdit && !state.isSetToEdit) {
    return <LoadingBackdrop open />;
  }

  return (
    <>
      <LoadingBackdrop open={isSaving} />
      <form
        id="author-form"
        onSubmit={handleSubmit}
        onKeyDown={preventEnterFormSubmission}
        autoComplete="off"
        style={{ minWidth: 0 }}
      >
        {/* 01 Basic info */}
        <FormSection title="Basic info" num="01">
          <FieldRow cols={2} minColWidth={240}>
            <FieldBlock label="Author name" required htmlFor="f-au-name">
              <TextInput
                id="f-au-name"
                name="author_name"
                value={state.author_name}
                onChange={handleFieldChange}
                required
              />
            </FieldBlock>
            <FieldBlock label="Author title" hint="e.g. “Founder · TTS”" htmlFor="f-au-title">
              <TextInput
                id="f-au-title"
                name="author_title"
                value={state.author_title || ''}
                onChange={handleFieldChange}
              />
            </FieldBlock>
          </FieldRow>
        </FormSection>

        {/* 02 Social */}
        <FormSection title="Social media" num="02">
          <FieldRow cols={3} minColWidth={200}>
            <FieldBlock label="Facebook URL" htmlFor="f-au-fb">
              <TextInput
                id="f-au-fb"
                name="author_facebook_url"
                value={state.author_facebook_url || ''}
                onChange={handleFieldChange}
                placeholder="https://facebook.com/…"
              />
            </FieldBlock>
            <FieldBlock label="X URL" htmlFor="f-au-tw">
              <TextInput
                id="f-au-tw"
                name="author_twitter_url"
                value={state.author_twitter_url || ''}
                onChange={handleFieldChange}
                placeholder="https://x.com/…"
              />
            </FieldBlock>
            <FieldBlock label="Instagram URL" htmlFor="f-au-ig">
              <TextInput
                id="f-au-ig"
                name="author_instagram_url"
                value={state.author_instagram_url || ''}
                onChange={handleFieldChange}
                placeholder="https://instagram.com/…"
              />
            </FieldBlock>
          </FieldRow>
        </FormSection>

        {/* 03 Images */}
        <FormSection title="Images" num="03">
          <FieldRow cols={2} minColWidth={240}>
            <UploadTile
              label="Profile picture"
              value={state.author_image_preview || state.author_image}
              accept="image/*"
              onChange={handleImageUpload}
              onRemove={() => {
                if (state.author_image_preview) window.URL.revokeObjectURL(state.author_image_preview);
                setState((prev) => ({ ...prev, author_image: '', author_image_preview: null }));
              }}
            />
            <UploadTile
              label="Signature"
              hint="Transparent PNG works best"
              value={state.author_signature_preview || state.author_signature}
              accept="image/*"
              onChange={handleSignatureUpload}
              onRemove={() => {
                if (state.author_signature_preview) window.URL.revokeObjectURL(state.author_signature_preview);
                setState((prev) => ({ ...prev, author_signature: '', author_signature_preview: null }));
              }}
            />
          </FieldRow>
        </FormSection>

        {/* 04 Bio */}
        <FormSection title="Bio" num="04">
          <FieldBlock
            label="Author bio"
            hint="Rich text — shown on the author's profile screen"
            noMargin
          >
            <div
              style={{
                background: '#FAF4EA',
                border: '1px solid #E8E0CF',
                borderRadius: 8,
                padding: 4,
              }}
            >
              <Editor
                ref={editorRef}
                defaultValue={state.author_bio}
                onTextChange={handleBioChange}
                onSelectionChange={handleSelectionChange}
              />
            </div>
          </FieldBlock>
        </FormSection>

        {/* 05 Caption aliases (edit-only) */}
        {isEdit && (
          <FormSection
            title="Caption aliases"
            num="05"
            right={
              <span className="text-text-muted" style={{ fontSize: 11.5 }}>
                Whisper / AssemblyAI corrections
              </span>
            }
          >
            <AuthorCaptionAliases authorId={selectedId} />
          </FormSection>
        )}

        {/* 06 Danger zone */}
        {isEdit && (
          <FormSection title="Danger zone" num="06">
            <div className="flex items-center" style={{ gap: 12, flexWrap: 'wrap' }}>
              <Button variant="danger" onClick={handleDelete} type="button">
                Delete author
              </Button>
              <span className="text-text-muted" style={{ fontSize: 12 }}>
                Permanent — cannot be undone.
              </span>
            </div>
          </FormSection>
        )}
      </form>
    </>
  );
});

export default AddEditAuthorV2;
